import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';

@Component({
  selector: 'app-memo',
  templateUrl: './memo.component.html',
  styleUrls: ['./memo.component.scss'],
  standalone: false,
})
export class MemoComponent implements OnInit {
  @Input() memoControl: FormControl;
  private _control$: Observable<FormControl>;

  @Input() isMemoValid = true;
  @Input() isInputValid = true;
  @Input() isLimitValid = true;

  memoIsTruncated: boolean;
  memoRegexValidation: string;
  memoValidationMaxLength: number;
  memoRegexSubscription: Subscription;
  memoValidationMaxLengthSubscription: Subscription;
  @HostBinding('class.readonly') @Input() readonly = false;
  @ViewChild('memo', { static: false }) private input: ElementRef<HTMLInputElement>;

  fullRegex = /^[\s-_A-Za-z0-9\u4E00-\u9FAF\uFF66-\uFF9F\u30A1-\u30FC\u3041-\u3096\u309D-\u309F]{0,30}$/g;
  inputRegex = /^[\s-_A-Za-z0-9\u4E00-\u9FAF\uFF66-\uFF9F\u30A1-\u30FC\u3041-\u3096\u309D-\u309F]*$/g;
  inputLimit = 30;

  onChange() {
    const newValue = this.input.nativeElement.value;
    if (newValue != null) {
      this.isMemoValid = newValue.match(this.fullRegex) ? true : false;
      if (this.memoRegexValidation || this.memoValidationMaxLength || this.inputRegex) {
        this.isLimitValid = newValue.length <= this.memoValidationMaxLength ? true : false;
        this.isInputValid = newValue.match(this.memoRegexValidation) || newValue.match(this.inputRegex) ? true : false;
        this.isMemoValid = this.isLimitValid && this.isInputValid ? true : false;
        if (!this.isMemoValid) {
          this.memoControl.setErrors({ memoErr: true });
        } else {
          this.memoControl.setErrors(null);
          this.memoControl.setValue(newValue);
        }
      } else {
        if (!this.isMemoValid) {
          this.memoControl.setErrors({ memoErr: true });
          this.isLimitValid = newValue.length <= this.inputLimit ? true : false;
          this.isInputValid = newValue.match(this.inputRegex) ? true : false;
        } else {
          this.memoControl.setErrors(null);
          this.memoControl.setValue(newValue);
        }
      }
    }
    this.memoControl.markAsDirty();
    this.memoControl.markAsTouched();
  }

  getControl(): Observable<FormControl> {
    if (!this._control$ && this.memoControl !== undefined) {
      this._control$ = this.memoControl.valueChanges.pipe(
        distinctUntilChanged(),
        startWith(this.memoControl.value),
        tap((value) => {
          this.memoControl.setValue(value, { emitEvent: false });
        }),
        map(() => this.memoControl)
      );
    }
    return this._control$;
  }

  showMemoTooltip(event: any, tooltip: MatTooltip, ms?: number): void {
    this.isMemoTruncated(event);
    if (event.type === 'click' && this.memoIsTruncated) {
      tooltip.show();
      setTimeout(() => {
        tooltip.hide();
        if (this.readonly) event.target.blur();
      }, ms);
    }
  }

  isMemoTruncated(event: any): boolean {
    this.memoIsTruncated = event.target.scrollWidth > event.target.offsetWidth ? true : false;
    return this.memoIsTruncated;
  }

  showMemoPlaceholder(): boolean {
    if (this.memoControl !== undefined) {
      return this.readonly || (this.memoControl.disabled && !this.readonly) ? true : false;
    }
  }

  isMemoRegexValidation = () => {
    this.memoRegexSubscription = this.getMemoRegexConfig$().subscribe((regexValidation) => {
      this.memoRegexValidation = regexValidation;
      this.cdr.detectChanges();
    });
  };

  getMemoRegexConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.validation.regex'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.validation.regex')
              .map((res: any) => res.value)[0]
        )
      );
  }

  isMemoMaxLength = () => {
    this.memoValidationMaxLengthSubscription = this.getMemoValidationMaxLengthConfig$().subscribe((memoMaxLength) => {
      this.memoValidationMaxLength = memoMaxLength;
      this.cdr.detectChanges();
    });
  };

  getMemoValidationMaxLengthConfig$(): Observable<number> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.validation.max.length'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.validation.max.length')
              .map((res: any) => res.value)[0]
        )
      );
  }

  constructor(private cdr: ChangeDetectorRef, private baseSiteConfigService: BaseSiteConfigService) {}

  ngOnInit() {
    this.isMemoRegexValidation();

    this.isMemoMaxLength();
  }
}
